import { useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Medal } from "lucide-react";

/**
 * Global milestone pop-up provider.
 *
 * Polls `/api/performance/milestones/feed` every 90 seconds for recent
 * unlocks. Server respects the audience / enabled flags, so if the caller
 * isn't allowed to see them the endpoint simply returns an empty `rows`.
 *
 * Mount ONCE at the top of the app (e.g. just under `<AuthProvider>`).
 */
export default function MilestoneToastProvider() {
  const { user } = useAuth();
  const seen = useRef(new Set());
  const initialized = useRef(false);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;

    const tick = async () => {
      try {
        const { data } = await api.get("/performance/milestones/feed", { params: { limit: 20 } });
        const rows = data?.rows || [];
        // On first load just index what already exists — don't spam the user
        // with historical milestones they've seen before. Subsequent polls
        // fire toasts only for fresh IDs.
        if (!initialized.current) {
          rows.forEach((m) => seen.current.add(m.id));
          initialized.current = true;
          return;
        }
        const duration = data?.duration_ms || 7000;
        rows.slice().reverse().forEach((m) => {
          if (seen.current.has(m.id)) return;
          seen.current.add(m.id);
          toast.custom(
            (tid) => (
              <div
                className="flex items-start gap-3 p-4 rounded-2xl bg-card border border-primary-200 shadow-soft max-w-sm"
                data-testid={`milestone-toast-${m.id}`}
                onClick={() => toast.dismiss(tid)}
              >
                <div className="h-10 w-10 grid place-items-center rounded-2xl bg-primary-50 text-primary-700 border border-primary-200 shrink-0">
                  <Medal className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-primary-700">
                    {m.tier_label} milestone · +{m.bonus_points} bonus
                  </p>
                  <p className="text-sm font-semibold mt-0.5 truncate">{m.user_name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">{m.label}</p>
                </div>
              </div>
            ),
            { duration },
          );
        });
      } catch {
        // Silent — notification fetch is non-critical.
      }
      if (!cancelled) {
        // eslint-disable-next-line no-use-before-define
        timer = setTimeout(tick, 90_000);
      }
    };

    let timer = setTimeout(tick, 1_500); // first poll shortly after mount
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [user]);

  return null;
}
